/**
 * Link do banner "Developed by Multipedidos" do rodapé do cardápio.
 *
 * Função PURA e fora do componente de propósito: a URL é um contrato com o
 * marketing (os UTMs identificam a origem de quem chega ao onboarding) e um
 * contrato com a nossa telemetria (o mesmo `slot` que vai no evento vai no
 * `utm_content`). Contrato tem de ser testável sem TestBed.
 *
 * Formato acordado com o marketing (02/09/2026):
 *
 *   ?utm_source=Pedir.Delivery
 *   &utm_medium=SoftwareMP
 *   &utm_id=Rodape+Pedir.Delivery
 *   &utm_campaign=<slug da loja>
 *
 * Detalhes que parecem cosméticos e não são:
 *
 *  - `utm_id` sai com `+`, LITERAL, como foi especificado. `+` em query string
 *    decodifica para espaço, então o GA lê "Rodape Pedir.Delivery" — que é o
 *    rótulo desejado. Passar isso por `encodeURIComponent` viraria `%2B` e o
 *    relatório mostraria o sinal de mais no meio do nome.
 *  - a `slug` (e SÓ ela) é escapada: é dado vindo do banco (`restaurant_url`) e
 *    um caractere reservado ali quebraria a query inteira.
 *  - sem slug, `utm_campaign` é OMITIDO em vez de sair vazio: uma campanha ''
 *    vira uma linha "(not set)" no relatório, indistinguível de erro de tagging.
 *  - `utm_content` distingue os DOIS gestos do rodapé (a logo e o botão de
 *    texto), que hoje apontam para o mesmo lugar. É o que permite responder
 *    "as pessoas clicam na imagem ou na chamada?" do lado do marketing — e é o
 *    mesmo valor que a telemetria manda em `in.slot`, para os dois lados
 *    baterem.
 */

/** Qual dos dois elementos clicáveis do rodapé. */
export type FooterBannerSlot = 'logo' | 'cta';

/**
 * O destino. UM: o onboarding, desde 16/10/2026 (de 21/09 até ali, a home
 * multipedidos.com.br).
 *
 * O "Crie seu Cardápio Também" agora leva direto para onde se cria a conta. A
 * raiz do onboarding responde 307 para uma das variantes de landing dele
 * (`/comecar`, `/v2`...) e PRESERVA a query string — conferido em 16/10/2026 —,
 * então os UTMs chegam intactos. Não aponte para a variante: quem sorteia é o
 * próprio onboarding (cookie `mp_lp`).
 *
 * Antes da home, havia um A/B 80/20 entre duas landings (`pedirdelivery-nv-preco`
 * e `lp-nova-versao-teste`) que deixaram de existir — a primeira virou um stub
 * cujo `<meta http-equiv="refresh">` de fallback apontava para `/` SEM a query
 * string, então quem caísse nele chegava sem UTM nenhum.
 *
 * A barra final faz parte: o `?` é concatenado direto.
 */
const LANDING = 'https://onboarding.multipedidos.com.br/';

/**
 * A chamada que o consumidor lê. UMA, para todo mundo, desde 21/09/2026.
 *
 * Até aqui o texto vinha junto com a variante sorteada: 80% liam "Conheça a
 * Multipedidos" e 20% "Crie seu Cardápio Também". O A/B rodava desde 18/07/2025
 * sem ninguém colher o resultado — não havia como. Com a telemetria do banner no
 * ar deu para medir, e o segundo texto ganhou de longe: **0,142% contra 0,093%**
 * de cliques por aparição do banner (1,54×, z = 7,4), sobre 1.838 cliques
 * humanos entre 09 e 20/09/2026.
 *
 * O que fecha o caso é o controle que estava ali por acaso: a LOGO é a mesma
 * imagem nas duas variantes, e ela EMPATOU (1,10×, z = 0,76). Só o braço em que
 * o texto mudava se moveu, então não foi viés de sorteio nem de amostra.
 *
 * O A/B de LANDING que acompanhava este texto acabou junto, por fora: as duas
 * páginas deixaram de existir (ver `LANDING`). Isso não contamina a leitura do
 * resultado acima — a landing é o DESTINO, e o consumidor não a vê antes de
 * clicar.
 */
export const FOOTER_BANNER_CTA_TEXT = 'Crie seu Cardápio Também';

/**
 * Monta a URL final do banner.
 *
 * @param slug `restaurant.info.restaurant_url` (ex.: `viaroma`)
 * @param slot qual elemento do rodapé leva a este link
 */
export function buildFooterBannerLink(
  slug: string,
  slot: FooterBannerSlot,
): string {
  const limpo = (slug || '').toString().trim();
  const params = [
    'utm_source=Pedir.Delivery',
    'utm_medium=SoftwareMP',
    // literal: ver o comentário do cabeçalho sobre o `+`.
    'utm_id=Rodape+Pedir.Delivery',
  ];
  if (limpo) params.push('utm_campaign=' + encodeURIComponent(limpo));
  params.push('utm_content=' + slot);
  return LANDING + '?' + params.join('&');
}
